import { Accordion, AccordionItem } from '@heroui/react';
import { Chip } from '@heroui/chip';
import cn from 'classnames';
import { LessonFormat } from '@/widgets/Lesson';
import { AwaitingReason, IAwaitingEnrollmentItem } from '@/entities/Enrollment';
import { COURSE_TYPE } from '@/shared/constants/courseData';
import { LessonTypeEnum } from '@/shared/types/lesson';
import { formatMinutesToHoursAndMinutes } from '@/shared/lib/utils/formatTime';

const STATUS_TEXT: Record<AwaitingReason, string> = {
  group: "You're enrolled! We're assigning you to a group, and your schedule will appear here soon.",
  schedule: 'The schedule for your group is being prepared and will appear here soon.',
};

const EMPTY_PROGRAMME_TEXT = 'The programme for this term will be available soon.';
const EMPTY_MODULE_TEXT = 'Sessions for this module will be added soon.';

// Бэкенд отдаёт подпись типа («Short Course»), регистр у неё не совпадает с константой
const isShortCourse = (typeCourse: string | null) =>
  typeCourse?.toLowerCase() === COURSE_TYPE.short.toLowerCase();

const getDescription = ({ term, groupNumber }: IAwaitingEnrollmentItem, isShort: boolean) => {
  const parts: string[] = [];
  if (!isShort && term?.number) {
    parts.push(`Term ${term.number}`);
  }
  if (groupNumber) {
    parts.push(`Group ${groupNumber}`);
  }
  return parts.join(', ');
};

interface AwaitingEnrollmentCardProps {
  item: IAwaitingEnrollmentItem;
}

/** Программа зачисленного курса без расписания: модули и сессии, в уроки не войти */
export const AwaitingEnrollmentCard = ({ item }: AwaitingEnrollmentCardProps) => {
  const typeCourse = (item.course.type && COURSE_TYPE[item.course.type]) || item.course.type;
  const isShort = isShortCourse(typeCourse);
  const description = getDescription(item, isShort);

  return (
    <div className="flex flex-col gap-y-4 shadow-info-card p-4 rounded-[0.5rem]">
      <div className="flex flex-col gap-y-2">
        <Chip
          className={cn('text-white bg-red', {
            ['bg-[#211D55]']: isShort,
          })}
        >
          {typeCourse}
        </Chip>
        <h3 className="text-lg font-bold">{`${item.course.title} ${item.intake.title}`}</h3>
        {description && <p className="font-medium">{description}</p>}
        <p className="text-grey-secondary">{STATUS_TEXT[item.reason]}</p>
      </div>

      {item.modules.length === 0 ? (
        <p className="rounded-lg bg-[#F9FAFB] px-4 py-7 text-grey-secondary">
          {EMPTY_PROGRAMME_TEXT}
        </p>
      ) : (
        <div className="w-full [&>div]:!px-0 [&>div]:!rounded-lg [&>div]:!overflow-hidden">
          <Accordion selectionMode="multiple">
            {item.modules.map((module) => {
              const totalDuration = module.sessions.reduce(
                (sum, session) => sum + session.duration,
                0,
              );
              return (
                <AccordionItem
                  key={module.intakeModuleId}
                  textValue={module.title}
                  classNames={{
                    trigger: 'cursor-pointer bg-[#F9FAFB] py-7 px-4 flex items-center gap-3',
                    indicator:
                      'order-first text-[#1E1E1E] w-5 h-5 min-w-5 min-h-5 flex shrink-0 [&_svg]:w-full [&_svg]:h-full [&_svg]:stroke-[2.5] [&_path]:stroke-[2.5]',
                    titleWrapper: 'flex-1 min-w-0',
                  }}
                  title={
                    <div className="flex justify-between items-center gap-2 w-full">
                      <p className="text-[20px] font-medium">{module.title}</p>
                      {module.sessions.length > 0 && (
                        <p className="text-sm">
                          {module.sessions.length}{' '}
                          {module.sessions.length === 1 ? 'session' : 'sessions'} &bull;{' '}
                          {formatMinutesToHoursAndMinutes(totalDuration)}
                        </p>
                      )}
                    </div>
                  }
                >
                  {module.sessions.length === 0 ? (
                    <p className="px-[30px] py-4 text-grey-secondary">{EMPTY_MODULE_TEXT}</p>
                  ) : (
                    <ul>
                      {module.sessions.map((session) => (
                        <li
                          key={session.id}
                          className="flex justify-between items-center gap-4 px-[30px] py-4"
                        >
                          <p>
                            {session.title}
                            {session.type === LessonTypeEnum.Exam && (
                              <span className="ml-2 text-sm text-grey-secondary">Exam</span>
                            )}
                          </p>
                          <div className="flex items-center gap-x-4">
                            <LessonFormat format={session.format} />
                            <p className="min-w-[56px]">
                              {formatMinutesToHoursAndMinutes(session.duration)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
};
